import React, { useState, useEffect, useRef } from 'react'
import styled from '@emotion/styled'
import Moment from 'moment-timezone'
import {
  colors,
  CHEF_PAY_CATEGORIES,
  CHEF_PAY_CATEGORY_OTHER_MANUAL,
} from '../../../constants'
import YSpacing from '@components/common/YSpacing'
import DividerLine from '@components/common/DividerLine'
import Button from '@components/common/form/Button'
import { AutocompleteInput } from '@containers/common/form'

// TypeScript types based on Go schema
export type ChefPayoutId = string

export interface VendorPricingAgreementTier {
  lowerLimit: string | null
  upperLimit?: string | null
  discountPercentage?: string | null
  fixedEntreePrice?: string | null
  fixedSidePrice?: string | null
  position: number
}

export type VendorPricingAgreementScope =
  | 'universal'
  | 'market'
  | 'vendor_market'
export type ServiceType = 'catering' | 'group_order'
export type DiscountType = 'percentage' | 'fixed_price'

export interface VendorPricingAgreement {
  id: string
  scope: VendorPricingAgreementScope
  serviceTypes: ServiceType[]
  vendorId?: string
  hqId?: number
  discountType: DiscountType
  tiers: VendorPricingAgreementTier[]
  active: boolean
  lastUpdatedBy: string
  createdAt: string
  updatedAt: string
}

export interface ChefVolumeRebate {
  id: string
  amount: string | null
  appliedVolumeTier: VendorPricingAgreementTier | null
  updatedAt: string
}

export interface VolumeRebateTemplate {
  isPercentageDiscount: boolean
  isFixedPrice: boolean
  amount?: string | null
  entreePrice?: string | null
  sidePrice?: string | null
}

export interface ChefPayout {
  id: ChefPayoutId
  orderId: string
  chefId: string
  payToName: string
  hqId: number
  date: string
  orderNumber: string
  headCount: number
  updatedAt: string
  grossPay: string | null
  processingFeeRate: string | null
  processingFee: string | null
  isTaxable: boolean
  tax: string | null
  dailyGrossVolume: string | null
  vendorPricingAgreement: VendorPricingAgreement | null
  volumeRebate: ChefVolumeRebate | null
  volumeRebateTemplate: VolumeRebateTemplate | null
  totalPayout: string | null
  hasOverrides: boolean // TODO - do we still need this?
  chefPayCategory?: string | null
  notes?: string | null
}

export interface ChefPayoutOverride {
  chefId: string
  createdAt: string
  updatedAt: string
  userEmail?: string | null
  headCount?: number
  grossPay?: string | null
  processingFee?: string | null
  tax?: string | null
  volumeRebateAmount?: string | null
  totalPayout?: string | null
  chefPayCategory?: string | null
  notes?: string | null
}

export interface ChefWithPayout {
  id: string
  name: string
  email?: string
  pickupTime?: string
  chefStatus?: any
  cutoffEmailSentAt?: string
  labelsEmailSentAt?: string
  chefPayout?: ChefPayout
  chefPayoutOverride?: ChefPayoutOverride
}

export interface AdditionalChefPayout {
  chefId: string
  name: string
  email: string
  isOutOfNetworkPartner: boolean
  phone: string
  chefPayout: ChefPayout | null
  userEmail?: string | null
  updatedAt?: string | null
}

interface ChefPayoutsTabProps {
  chefs: ChefWithPayout[]
  additionalChefPayouts?: AdditionalChefPayout[]
  groupOrderId?: string
  foodTaxRates: number[]
  isTaxable: boolean
  loadChefs?: (filters: any) => Promise<any[]>
  loadIndexChefsByIds?: (chefIds: string[]) => Promise<any[]>
  updateChefPayoutOverrides?: (request: any) => Promise<any | null>
  onPayoutUpdated?: (updatedGroupOrder: any) => void
  displayWarningMessage: (message: string) => void
}

interface EditingState {
  headCount?: number
  grossPay?: string
  processingFee?: string
  tax?: string
  volumeRebateAmount?: string
  totalPayout?: string
  isManualProcessingFee?: boolean
  isManualVolumeRebate?: boolean
  chefPayCategory?: string
  notes?: string
}

interface ChefEdit {
  values: EditingState
  isDeleting: boolean
  processingFeeRate?: string | null
  errorFetchingProcessingFeeRate?: boolean
}

interface AdditionalChefPayoutEdit {
  chefId: string
  name: string
  email: string
  phone: string
  isOutOfNetworkPartner: boolean
  values: EditingState
  isNew: boolean
  isDeleting: boolean
  processingFeeRate?: string | null
  errorFetchingProcessingFeeRate?: boolean
}

const ChefPayoutsTab: React.FC<ChefPayoutsTabProps> = ({
  chefs,
  additionalChefPayouts = [],
  groupOrderId,
  foodTaxRates,
  isTaxable,
  loadChefs,
  loadIndexChefsByIds,
  updateChefPayoutOverrides,
  onPayoutUpdated,
  displayWarningMessage,
}) => {
  const [expandedChefs, setExpandedChefs] = useState<Set<string>>(new Set())
  const [expandedSections, setExpandedSections] = useState<
    Record<string, Set<string>>
  >({})
  const [editingChefs, setEditingChefs] = useState<Record<string, ChefEdit>>({})
  const [editingAdditionalChefs, setEditingAdditionalChefs] = useState<
    Record<string, AdditionalChefPayoutEdit>
  >({})
  const [showAddAdditionalChef, setShowAddAdditionalChef] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const pendingSaveRef = useRef(false)

  // Calculate total tax rate
  const totalTaxRate = foodTaxRates.reduce((sum, rate) => sum + rate, 0)

  // Clear editing state when chef data is updated after save
  useEffect(() => {
    if (pendingSaveRef.current && !isSaving) {
      setEditingChefs({})
      setEditingAdditionalChefs({})
      pendingSaveRef.current = false
    }
  }, [chefs, additionalChefPayouts, isSaving])

  // Clean up editing state for chefs that no longer exist
  useEffect(() => {
    const chefIds = new Set(chefs.map((chef) => chef.id))
    setEditingChefs((prev) => {
      const filtered: Record<string, ChefEdit> = {}
      Object.entries(prev).forEach(([chefId, edit]) => {
        if (chefIds.has(chefId)) {
          filtered[chefId] = edit
        }
      })

      return Object.keys(filtered).length === Object.keys(prev).length
        ? prev
        : filtered
    })
  }, [chefs])

  const toggleChef = (chefId: string) => {
    setExpandedChefs((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(chefId)) {
        newSet.delete(chefId)
      } else {
        newSet.add(chefId)
      }

      return newSet
    })
  }

  const toggleSection = (chefId: string, sectionName: string) => {
    setExpandedSections((prev) => {
      const chefSections = new Set(prev[chefId] || [])
      if (chefSections.has(sectionName)) {
        chefSections.delete(sectionName)
      } else {
        chefSections.add(sectionName)
      }

      return { ...prev, [chefId]: chefSections }
    })
  }

  const isSectionExpanded = (chefId: string, sectionName: string): boolean => {
    return expandedSections[chefId]?.has(sectionName) || false
  }

  // Consolidated function to fetch processing fee rate for a chef
  const fetchProcessingFeeRate = async (
    chefId: string,
    fallbackRate: string | null = null,
  ): Promise<{ rate: string | null; error: boolean }> => {
    if (!loadIndexChefsByIds) {
      return { rate: fallbackRate, error: false }
    }

    try {
      const chefData = await loadIndexChefsByIds([chefId])
      if (chefData && chefData.length > 0) {
        return { rate: chefData[0].processingFee, error: false }
      }

      return { rate: fallbackRate, error: false }
    } catch (error) {
      return { rate: fallbackRate, error: true }
    }
  }

  // Consolidated function to calculate processing fee based on gross pay and rate
  const calculateProcessingFee = (
    grossPay: string,
    processingFeeRate: string | null,
  ): string => {
    if (!processingFeeRate) {
      return '0'
    }
    const grossPayAmount = parseFloat(grossPay) || 0
    const rate = parseFloat(processingFeeRate)
    const calculatedFee = -1 * (grossPayAmount * rate)

    return calculatedFee.toFixed(2)
  }

  // Consolidated function to handle field updates with processing fee auto-calculation
  const handleFieldUpdate = <T extends ChefEdit | AdditionalChefPayoutEdit>(
    currentEdit: T,
    fieldName: keyof EditingState,
    value: string | number | undefined,
  ): Pick<T, 'values'> => {
    const { values: chefValues, processingFeeRate } = currentEdit
    const updatedValues = {
      ...chefValues,
      [fieldName]: value,
    }

    // Auto-calculate processing fee when gross pay changes (if not manually set)
    if (
      fieldName === 'grossPay' &&
      !updatedValues.isManualProcessingFee &&
      processingFeeRate
    ) {
      updatedValues.processingFee = calculateProcessingFee(
        value as string,
        processingFeeRate,
      )
    }

    // Recalculate total payout
    updatedValues.totalPayout = calculateTotalPayout(updatedValues)

    return {
      values: updatedValues,
    }
  }

  // Generic toggle function for manual override flags
  const toggleManualOverride = (
    chefId: string,
    isAdditional: boolean,
    fieldName: 'isManualProcessingFee' | 'isManualVolumeRebate',
    recalculateOnDisable: (
      chefEdit: ChefEdit | AdditionalChefPayoutEdit,
      newIsManual: boolean,
    ) => Partial<EditingState>,
  ) => {
    const setState = isAdditional ? setEditingAdditionalChefs : setEditingChefs

    setState((prev: any) => {
      const chefEdit = prev[chefId]
      const newIsManual = !chefEdit.values[fieldName]
      const updatedValues = {
        ...chefEdit.values,
        [fieldName]: newIsManual,
        ...recalculateOnDisable(chefEdit, newIsManual),
      }

      return {
        ...prev,
        [chefId]: {
          ...chefEdit,
          values: updatedValues,
        },
      }
    })
  }

  const toggleManualProcessingFee = (chefId: string, isAdditional = false) => {
    toggleManualOverride(
      chefId,
      isAdditional,
      'isManualProcessingFee',
      (chefEdit, newIsManual) => {
        // If unchecking, recalculate processing fee from gross pay and rate
        if (!newIsManual && chefEdit.processingFeeRate) {
          const processingFee = calculateProcessingFee(
            chefEdit.values.grossPay || '0',
            chefEdit.processingFeeRate,
          )
          const totalPayout = calculateTotalPayout({
            ...chefEdit.values,
            processingFee,
          })

          return { processingFee, totalPayout }
        }

        return {}
      },
    )
  }

  const toggleManualVolumeRebate = (chefId: string, isAdditional = false) => {
    toggleManualOverride(
      chefId,
      isAdditional,
      'isManualVolumeRebate',
      (chefEdit, newIsManual) => {
        if (!newIsManual) {
          // If unchecking, set volume rebate to undefined (let API apply standard tier)
          const totalPayout = calculateTotalPayout({
            ...chefEdit.values,
            volumeRebateAmount: undefined,
          })

          return { volumeRebateAmount: undefined, totalPayout }
        } else {
          // If checking for the first time, initialize to '0' if undefined
          if (chefEdit.values.volumeRebateAmount === undefined) {
            const totalPayout = calculateTotalPayout({
              ...chefEdit.values,
              volumeRebateAmount: '0',
            })

            return { volumeRebateAmount: '0', totalPayout }
          }
        }

        return {}
      },
    )
  }

  const startEditing = async (chef: ChefWithPayout) => {
    const payout = chef.chefPayout
    const override = chef.chefPayoutOverride

    // Fetch the latest processing fee rate for the chef
    const { rate: processingFeeRate, error: errorFetchingProcessingFeeRate } =
      await fetchProcessingFeeRate(chef.id, payout?.processingFeeRate ?? null)

    // Initialize with override values if they exist, otherwise use base payout values, or zeros if no payout
    const initialValues: EditingState = {
      headCount: override?.headCount ?? payout?.headCount ?? 0,
      grossPay: override?.grossPay ?? payout?.grossPay ?? '0',
      processingFee: override?.processingFee ?? payout?.processingFee ?? '0',
      tax: override?.tax ?? payout?.tax ?? '0',
      volumeRebateAmount:
        override?.volumeRebateAmount ?? payout?.volumeRebate?.amount ?? '0',
      totalPayout: '', // Will be calculated
      isManualProcessingFee:
        override?.processingFee !== undefined &&
        override?.processingFee !== null,
      isManualVolumeRebate:
        override?.volumeRebateAmount !== undefined &&
        override?.volumeRebateAmount !== null,
      chefPayCategory: override?.chefPayCategory ?? '',
      notes: override?.notes ?? '',
    }

    // Calculate the initial total payout
    initialValues.totalPayout = calculateTotalPayout(initialValues)

    setEditingChefs((prev) => ({
      ...prev,
      [chef.id]: {
        values: initialValues,
        isDeleting: false,
        processingFeeRate,
        errorFetchingProcessingFeeRate,
      },
    }))
    // Auto-expand the payout details section
    setExpandedSections((prev) => ({
      ...prev,
      [chef.id]: new Set([...(prev[chef.id] || []), 'payoutDetails']),
    }))
  }

  const cancelEditing = (chefId: string) => {
    setEditingChefs((prev) => {
      const updated = { ...prev }
      delete updated[chefId]

      return updated
    })
  }

  const cancelAllEdits = () => {
    setEditingChefs({})
    setEditingAdditionalChefs({})
    setShowAddAdditionalChef(false)
  }

  const markForDeletion = (chefId: string) => {
    setEditingChefs((prev) => ({
      ...prev,
      [chefId]: {
        values: {},
        isDeleting: true,
      },
    }))
  }

  const calculateTotalPayout = (values: EditingState): string => {
    const grossPay = parseFloat(values.grossPay || '0')
    const processingFee = parseFloat(values.processingFee || '0')
    const tax = parseFloat(values.tax || '0')
    const volumeRebateAmount = parseFloat(values.volumeRebateAmount || '0')

    const total = grossPay + processingFee + tax + volumeRebateAmount

    return total.toFixed(2)
  }

  const calculateTax = (chefId: string) => {
    const chefEdit = editingChefs[chefId]
    if (!chefEdit || totalTaxRate === 0) {
      return
    }

    const grossPay = parseFloat(chefEdit.values.grossPay || '0')
    const processingFee = parseFloat(chefEdit.values.processingFee || '0')
    const volumeRebateAmount = parseFloat(
      chefEdit.values.volumeRebateAmount || '0',
    )

    const netAmount = grossPay + processingFee + volumeRebateAmount
    const calculatedTax = (netAmount * totalTaxRate).toFixed(2)

    setEditingChefs((prev) => ({
      ...prev,
      [chefId]: {
        ...prev[chefId],
        values: {
          ...prev[chefId].values,
          tax: calculatedTax,
          totalPayout: calculateTotalPayout({
            ...prev[chefId].values,
            tax: calculatedTax,
          }),
        },
      },
    }))
  }

  const updateChefValue = (
    chefId: string,
    fieldName: keyof EditingState,
    value: string | number | undefined,
  ) => {
    setEditingChefs((prev) => {
      const chefEdit = prev[chefId]
      const updated = handleFieldUpdate(chefEdit, fieldName, value)

      return {
        ...prev,
        [chefId]: {
          ...prev[chefId],
          ...updated,
        },
      }
    })
  }

  const hasAnyChanges = () => {
    return (
      Object.keys(editingChefs).length > 0 ||
      Object.keys(editingAdditionalChefs).length > 0
    )
  }

  // Validation helpers for chef pay category and notes
  const isNotesRequired = (category: string | undefined): boolean => {
    return category === CHEF_PAY_CATEGORY_OTHER_MANUAL
  }

  const isNotesValid = (
    category: string | undefined,
    notes: string | undefined,
  ): boolean => {
    if (!isNotesRequired(category)) {
      return true
    }
    const trimmedNotes = (notes || '').trim()

    return trimmedNotes.length >= 5
  }

  const isCategoryValid = (
    category: string | undefined,
    isDeleting: boolean,
  ): boolean => {
    if (isDeleting) {
      return true
    }

    return !!category && category.trim().length > 0
  }

  const getChefValidationErrors = (
    chefId: string,
    isAdditional: boolean,
  ): string[] => {
    const errors: string[] = []
    const edit = isAdditional
      ? editingAdditionalChefs[chefId]
      : editingChefs[chefId]

    if (!edit || edit.isDeleting) {
      return errors
    }

    if (!isCategoryValid(edit.values.chefPayCategory, false)) {
      errors.push('Chef Pay Category is required')
    }

    if (!isNotesValid(edit.values.chefPayCategory, edit.values.notes)) {
      errors.push(
        'Notes are required (min 5 characters) when category is "Other – Manual adjustment"',
      )
    }

    return errors
  }

  const hasValidationErrors = (): boolean => {
    // Check regular chef payouts
    for (const chefId of Object.keys(editingChefs)) {
      if (getChefValidationErrors(chefId, false).length > 0) {
        return true
      }
    }

    // Check additional chef payouts
    for (const chefId of Object.keys(editingAdditionalChefs)) {
      if (getChefValidationErrors(chefId, true).length > 0) {
        return true
      }
    }

    return false
  }

  const startEditingAdditional = async (additional: AdditionalChefPayout) => {
    const payout = additional.chefPayout

    if (!payout) {
      return
    }

    // Fetch the latest processing fee rate for the chef
    const { rate: processingFeeRate, error: errorFetchingProcessingFeeRate } =
      await fetchProcessingFeeRate(
        additional.chefId,
        payout.processingFeeRate ?? null,
      )

    const initialValues: EditingState = {
      headCount: payout.headCount,
      grossPay: payout.grossPay ?? '',
      processingFee: payout.processingFee ?? '',
      tax: payout.tax ?? '',
      volumeRebateAmount: payout.volumeRebate?.amount ?? '',
      totalPayout: '',
      isManualProcessingFee: false,
      isManualVolumeRebate: false,
      chefPayCategory: payout.chefPayCategory ?? '',
      notes: payout.notes ?? '',
    }

    initialValues.totalPayout = calculateTotalPayout(initialValues)

    setEditingAdditionalChefs((prev) => ({
      ...prev,
      [additional.chefId]: {
        chefId: additional.chefId,
        name: additional.name,
        email: additional.email,
        phone: additional.phone,
        isOutOfNetworkPartner: additional.isOutOfNetworkPartner,
        values: initialValues,
        isNew: false,
        isDeleting: false,
        processingFeeRate,
        errorFetchingProcessingFeeRate,
      },
    }))
  }

  const addNewAdditionalChef = async (chefData: {
    chefId: string
    name: string
    email: string
    phone: string
  }) => {
    // Check if chef already exists
    const existingChef = chefs.find((c) => c.id === chefData.chefId)
    const existingAdditional = additionalChefPayouts.find(
      (c) => c.chefId === chefData.chefId,
    )

    if (existingChef || existingAdditional) {
      displayWarningMessage('This chef already has a payout on this order')

      return
    }

    // Fetch the latest processing fee rate for the chef
    const { rate: processingFeeRate, error: errorFetchingProcessingFeeRate } =
      await fetchProcessingFeeRate(chefData.chefId, null)

    const initialValues: EditingState = {
      headCount: 0,
      grossPay: '0',
      processingFee: '0',
      tax: '0',
      volumeRebateAmount: '0',
      totalPayout: '0',
      isManualProcessingFee: false,
      isManualVolumeRebate: false,
      chefPayCategory: '',
      notes: '',
    }

    setEditingAdditionalChefs((prev) => ({
      ...prev,
      [chefData.chefId]: {
        ...chefData,
        isOutOfNetworkPartner: false,
        values: initialValues,
        isNew: true,
        isDeleting: false,
        processingFeeRate,
        errorFetchingProcessingFeeRate,
      },
    }))
    setShowAddAdditionalChef(false)
  }

  const markAdditionalForDeletion = (chefId: string) => {
    setEditingAdditionalChefs((prev) => ({
      ...prev,
      [chefId]: {
        ...prev[chefId],
        isDeleting: true,
      },
    }))
  }

  const cancelEditingAdditional = (chefId: string) => {
    setEditingAdditionalChefs((prev) => {
      const updated = { ...prev }
      delete updated[chefId]

      return updated
    })
  }

  const updateAdditionalChefValue = (
    chefId: string,
    fieldName: keyof EditingState,
    value: string | number | undefined,
  ) => {
    setEditingAdditionalChefs((prev) => {
      const chefEdit = prev[chefId]
      const updated = handleFieldUpdate(chefEdit, fieldName, value)

      return {
        ...prev,
        [chefId]: {
          ...prev[chefId],
          ...updated,
        },
      }
    })
  }

  const calculateTaxForAdditional = (chefId: string) => {
    const chefEdit = editingAdditionalChefs[chefId]
    if (!chefEdit || totalTaxRate === 0) {
      return
    }

    const grossPay = parseFloat(chefEdit.values.grossPay || '0')
    const processingFee = parseFloat(chefEdit.values.processingFee || '0')
    const volumeRebateAmount = parseFloat(
      chefEdit.values.volumeRebateAmount || '0',
    )

    const netAmount = grossPay + processingFee + volumeRebateAmount
    const calculatedTax = (netAmount * totalTaxRate).toFixed(2)

    setEditingAdditionalChefs((prev) => ({
      ...prev,
      [chefId]: {
        ...prev[chefId],
        values: {
          ...prev[chefId].values,
          tax: calculatedTax,
          totalPayout: calculateTotalPayout({
            ...prev[chefId].values,
            tax: calculatedTax,
          }),
        },
      },
    }))
  }

  const renderChefPayCategoryAndNotes = (
    chefId: string,
    isAdditional: boolean,
  ) => {
    const chefEdit = isAdditional
      ? editingAdditionalChefs[chefId]
      : editingChefs[chefId]

    if (!chefEdit || chefEdit.isDeleting) {
      return null
    }

    const { chefPayCategory, notes } = chefEdit.values
    const validationErrors = getChefValidationErrors(chefId, isAdditional)

    const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newCategory = e.target.value
      if (isAdditional) {
        setEditingAdditionalChefs((prev) => ({
          ...prev,
          [chefId]: {
            ...prev[chefId],
            values: {
              ...prev[chefId].values,
              chefPayCategory: newCategory,
            },
          },
        }))
      } else {
        setEditingChefs((prev) => ({
          ...prev,
          [chefId]: {
            ...prev[chefId],
            values: {
              ...prev[chefId].values,
              chefPayCategory: newCategory,
            },
          },
        }))
      }
    }

    const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newNotes = e.target.value
      if (isAdditional) {
        setEditingAdditionalChefs((prev) => ({
          ...prev,
          [chefId]: {
            ...prev[chefId],
            values: {
              ...prev[chefId].values,
              notes: newNotes,
            },
          },
        }))
      } else {
        setEditingChefs((prev) => ({
          ...prev,
          [chefId]: {
            ...prev[chefId],
            values: {
              ...prev[chefId].values,
              notes: newNotes,
            },
          },
        }))
      }
    }

    return (
      <CategoryNotesContainer>
        <CategoryNotesTitle>Adjustment Details</CategoryNotesTitle>
        <CategorySelectWrapper>
          <CategorySelect
            value={chefPayCategory || ''}
            onChange={handleCategoryChange}
            hasError={!isCategoryValid(chefPayCategory, false)}
          >
            <option value="">-- Select Category --</option>
            {CHEF_PAY_CATEGORIES.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </CategorySelect>
          <SelectFieldLabel>Chef Pay Category *</SelectFieldLabel>
        </CategorySelectWrapper>

        <NotesWrapper>
          <NotesTextarea
            placeholder="Enter notes explaining the adjustment (min 5 characters)..."
            value={notes || ''}
            onChange={handleNotesChange}
            hasError={!isNotesValid(chefPayCategory, notes)}
          />
          <NotesFieldLabel>
            Notes * (required for &quot;Other – Manual adjustment&quot;)
          </NotesFieldLabel>
          <NotesCharCount isError={(notes || '').trim().length < 5}>
            {(notes || '').trim().length} / 5 min characters
          </NotesCharCount>
        </NotesWrapper>

        {validationErrors.length > 0 && (
          <ValidationErrorsContainer>
            {validationErrors.map((error, idx) => (
              <ValidationErrorItem key={idx}>{error}</ValidationErrorItem>
            ))}
          </ValidationErrorsContainer>
        )}
      </CategoryNotesContainer>
    )
  }

  const saveAllOverrides = async () => {
    if (!groupOrderId || !updateChefPayoutOverrides || !hasAnyChanges()) {
      return
    }

    // Check for validation errors before saving
    if (hasValidationErrors()) {
      displayWarningMessage(
        'Please fix validation errors before saving. Chef pay category is required for all overrides and additional payouts, and Notes are required when category is "Other – Manual adjustment".',
      )

      return
    }

    setIsSaving(true)
    pendingSaveRef.current = true

    try {
      const payoutOverrides = Object.entries(editingChefs).map(
        ([chefId, edit]) => {
          if (edit.isDeleting) {
            return {
              chefId,
              deleteOverride: true,
            }
          }

          return {
            chefId,
            deleteOverride: false,
            headCount: edit.values.headCount,
            grossPay: edit.values.grossPay || null,
            processingFee: edit.values.isManualProcessingFee
              ? edit.values.processingFee || null
              : null,
            tax: edit.values.tax || null,
            volumeRebateAmount: edit.values.isManualVolumeRebate
              ? edit.values.volumeRebateAmount || null
              : undefined,
            chefPayCategory: edit.values.chefPayCategory,
            notes: edit.values.notes || undefined,
          }
        },
      )

      // Prepare additional chef payouts
      const createAdditionalChefPayouts: any[] = []
      const updateAdditionalChefPayouts: any[] = []
      const deleteAdditionalChefPayoutChefIds: string[] = []

      Object.entries(editingAdditionalChefs).forEach(([chefId, edit]) => {
        if (edit.isDeleting) {
          deleteAdditionalChefPayoutChefIds.push(chefId)
        } else if (edit.isNew) {
          createAdditionalChefPayouts.push({
            chefId,
            headCount: edit.values.headCount || 0,
            grossPay: edit.values.grossPay || '0',
            processingFee: edit.values.isManualProcessingFee
              ? edit.values.processingFee || '0'
              : null,
            tax: edit.values.tax || '0',
            volumeRebate: edit.values.isManualVolumeRebate
              ? edit.values.volumeRebateAmount || '0'
              : undefined,
            totalPayout: edit.values.totalPayout || '0',
            chefPayCategory: edit.values.chefPayCategory,
            notes: edit.values.notes || undefined,
          })
        } else {
          updateAdditionalChefPayouts.push({
            chefId,
            headCount: edit.values.headCount,
            grossPay: edit.values.grossPay || null,
            processingFee: edit.values.isManualProcessingFee
              ? edit.values.processingFee || null
              : null,
            tax: edit.values.tax || null,
            volumeRebateAmount: edit.values.isManualVolumeRebate
              ? edit.values.volumeRebateAmount || null
              : undefined,
            chefPayCategory: edit.values.chefPayCategory,
            notes: edit.values.notes || undefined,
          })
        }
      })

      const request: any = {
        orderId: groupOrderId,
        payoutOverrides,
      }

      if (createAdditionalChefPayouts.length > 0) {
        request.createAdditionalChefPayouts = createAdditionalChefPayouts
      }
      if (updateAdditionalChefPayouts.length > 0) {
        request.updateAdditionalChefPayouts = updateAdditionalChefPayouts
      }
      if (deleteAdditionalChefPayoutChefIds.length > 0) {
        request.deleteAdditionalChefPayoutChefIds =
          deleteAdditionalChefPayoutChefIds
      }

      const updatedGroupOrder = await updateChefPayoutOverrides(request)

      if (updatedGroupOrder && onPayoutUpdated) {
        onPayoutUpdated(updatedGroupOrder)
      }
      // Don't clear editing state here - let useEffect handle it after props update
    } catch (error) {
      // If there's an error, don't clear on next update
      pendingSaveRef.current = false
      throw error
    } finally {
      setIsSaving(false)
    }
  }

  const formatCurrency = (value: string | null | undefined): string => {
    if (!value) {
      return '$0.00'
    }
    const num = parseFloat(value)

    return isNaN(num) ? '$0.00' : `$${num.toFixed(2)}`
  }

  const formatDate = (date: string | undefined): string => {
    if (!date) {
      return 'N/A'
    }

    return Moment(date).format('MMM D YYYY, h:mm:ss a')
  }

  const renderValue = (
    label: string,
    baseValue: string | number | boolean | null | undefined,
    overrideValue?: string | number | null | undefined,
    isCurrency = false,
  ) => {
    const hasOverride = overrideValue !== undefined && overrideValue !== null
    const displayBase = isCurrency
      ? formatCurrency(baseValue as string)
      : baseValue?.toString() || 'N/A'
    const displayOverride = isCurrency
      ? formatCurrency(overrideValue as string)
      : overrideValue?.toString() || 'N/A'

    return (
      <ValueRow>
        <Label>{label}:</Label>
        <ValueContainer>
          <BaseValue hasOverride={hasOverride}>{displayBase}</BaseValue>
          {hasOverride && (
            <>
              <Arrow>→</Arrow>
              <OverrideValue>{displayOverride}</OverrideValue>
            </>
          )}
        </ValueContainer>
      </ValueRow>
    )
  }

  const renderEditableField = (
    chefId: string,
    label: string,
    fieldName: keyof EditingState,
    isCurrency = false,
    isReadOnly = false,
    shouldBeNegative = false,
  ) => {
    const chefEdit = editingChefs[chefId]
    if (!chefEdit) {
      return null
    }

    // Special handling for processing fee with checkbox
    if (fieldName === 'processingFee' && isCurrency) {
      const currentValue = parseFloat(chefEdit.values[fieldName] as string) || 0
      const isManual = chefEdit.values.isManualProcessingFee || false
      const isDisabled = !isManual

      return (
        <EditFieldWrapper key={fieldName}>
          <CurrencyInputStyled
            type="number"
            placeholder={label}
            value={chefEdit.values[fieldName]?.toString() || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const { value } = e.target
              let processedValue = value

              if (shouldBeNegative && value && parseFloat(value) > 0) {
                processedValue = (-Math.abs(parseFloat(value))).toString()
              }

              updateChefValue(chefId, fieldName, processedValue)
            }}
            disabled={isDisabled}
          />
          <FieldLabel>{label}</FieldLabel>
          <CheckboxWrapper>
            <Checkbox
              type="checkbox"
              id={`manual-processing-fee-${chefId}`}
              checked={isManual}
              onChange={() => toggleManualProcessingFee(chefId, false)}
            />
            <CheckboxLabel htmlFor={`manual-processing-fee-${chefId}`}>
              Manually override processing fee
            </CheckboxLabel>
          </CheckboxWrapper>
          {shouldBeNegative && currentValue > 0 && (
            <ValidationError>Must be zero or negative</ValidationError>
          )}
          {chefEdit.errorFetchingProcessingFeeRate ? (
            <ProcessingFeeError>
              ⚠️ Unable to fetch the processing fee rate for this chef. Please
              enter the processing fee manually.
            </ProcessingFeeError>
          ) : chefEdit.processingFeeRate ? (
            <ProcessingFeeInfo>
              Processing Fee Rate:{' '}
              {(parseFloat(chefEdit.processingFeeRate) * 100).toFixed(2)}%
              {!isManual && <> • Auto-calculated from gross payout</>}
              {isManual && <> • Manually overridden</>}
            </ProcessingFeeInfo>
          ) : (
            <ProcessingFeeWarning>
              No processing fee rate set for this chef.
            </ProcessingFeeWarning>
          )}
        </EditFieldWrapper>
      )
    }

    // Special handling for volume rebate with checkbox
    if (fieldName === 'volumeRebateAmount' && isCurrency) {
      const currentValue = parseFloat(chefEdit.values[fieldName] as string) || 0
      const isManual = chefEdit.values.isManualVolumeRebate || false
      const isDisabled = !isManual

      return (
        <EditFieldWrapper key={fieldName}>
          <CurrencyInputStyled
            type="number"
            placeholder={label}
            value={isManual ? chefEdit.values[fieldName]?.toString() || '' : ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const { value } = e.target
              let processedValue = value

              if (shouldBeNegative && value && parseFloat(value) > 0) {
                processedValue = (-Math.abs(parseFloat(value))).toString()
              }

              updateChefValue(chefId, fieldName, processedValue)
            }}
            disabled={isDisabled}
          />
          <FieldLabel>{label}</FieldLabel>
          <CheckboxWrapper>
            <Checkbox
              type="checkbox"
              id={`manual-volume-rebate-${chefId}`}
              checked={isManual}
              onChange={() => toggleManualVolumeRebate(chefId, false)}
            />
            <CheckboxLabel htmlFor={`manual-volume-rebate-${chefId}`}>
              Manually override volume rebate
            </CheckboxLabel>
          </CheckboxWrapper>
          {shouldBeNegative && currentValue > 0 && (
            <ValidationError>Must be zero or negative</ValidationError>
          )}
          {isManual ? (
            <VolumeRebateInfo>
              Volume rebate amount is manually overridden
            </VolumeRebateInfo>
          ) : (
            <VolumeRebateInfo>
              Vendor&apos;s applicable volume rebate tier will be applied to
              this payout
            </VolumeRebateInfo>
          )}
        </EditFieldWrapper>
      )
    }

    if (isCurrency) {
      const currentValue = parseFloat(chefEdit.values[fieldName] as string) || 0

      return (
        <EditFieldWrapper key={fieldName}>
          <CurrencyInputStyled
            type="number"
            placeholder={label}
            value={chefEdit.values[fieldName]?.toString() || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const { value } = e.target
              let processedValue = value

              // Validate negative fields
              if (shouldBeNegative && value && parseFloat(value) > 0) {
                processedValue = (-Math.abs(parseFloat(value))).toString()
              }

              updateChefValue(chefId, fieldName, processedValue)
            }}
            disabled={isReadOnly}
          />
          <FieldLabel>{label}</FieldLabel>
          {shouldBeNegative && currentValue > 0 && (
            <ValidationError>Must be zero or negative</ValidationError>
          )}
        </EditFieldWrapper>
      )
    } else if (fieldName === 'headCount') {
      return (
        <EditFieldWrapper key={fieldName}>
          <CurrencyInputStyled
            type="number"
            placeholder={label}
            value={chefEdit.values[fieldName]?.toString() || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const { value } = e.target
              const parsedValue = value ? parseInt(value) : undefined
              updateChefValue(chefId, fieldName, parsedValue)
            }}
          />
          <FieldLabel>{label}</FieldLabel>
        </EditFieldWrapper>
      )
    }

    return null
  }

  const renderVendorPricingAgreement = (
    chefId: string,
    vpa: VendorPricingAgreement | null,
  ) => {
    if (!vpa) {
      return <EmptyState>No pricing agreement</EmptyState>
    }

    const sectionName = 'vendorPricing'
    const isExpanded = isSectionExpanded(chefId, sectionName)

    return (
      <SubSection>
        <CollapsibleHeader onClick={() => toggleSection(chefId, sectionName)}>
          <SectionHeaderLeft>
            <SmallExpandIcon isExpanded={isExpanded}>▶</SmallExpandIcon>
            <SubSectionTitle>Vendor Pricing Agreement</SubSectionTitle>
          </SectionHeaderLeft>
          <SectionHeaderRight>
            <SectionBadge>{vpa.discountType}</SectionBadge>
            <SectionBadge>{vpa.active ? 'Active' : 'Inactive'}</SectionBadge>
          </SectionHeaderRight>
        </CollapsibleHeader>

        {isExpanded && (
          <>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>ID:</InfoLabel>
                <InfoValue>{vpa.id}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Scope:</InfoLabel>
                <InfoValue>{vpa.scope}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Discount Type:</InfoLabel>
                <InfoValue>{vpa.discountType}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Active:</InfoLabel>
                <InfoValue>{vpa.active ? 'Yes' : 'No'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Service Types:</InfoLabel>
                <InfoValue>{vpa.serviceTypes.join(', ')}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Last Updated By:</InfoLabel>
                <InfoValue>{vpa.lastUpdatedBy}</InfoValue>
              </InfoItem>
            </InfoGrid>

            {vpa.tiers && vpa.tiers.length > 0 && (
              <>
                <TiersTitle>Pricing Tiers</TiersTitle>
                {vpa.tiers.map((tier, idx) => (
                  <TierCard key={idx}>
                    <TierHeader>Tier {tier.position}</TierHeader>
                    <InfoGrid>
                      <InfoItem>
                        <InfoLabel>Lower Limit:</InfoLabel>
                        <InfoValue>{formatCurrency(tier.lowerLimit)}</InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>Upper Limit:</InfoLabel>
                        <InfoValue>{formatCurrency(tier.upperLimit)}</InfoValue>
                      </InfoItem>
                      {tier.discountPercentage && (
                        <InfoItem>
                          <InfoLabel>Discount %:</InfoLabel>
                          <InfoValue>{tier.discountPercentage}%</InfoValue>
                        </InfoItem>
                      )}
                      {tier.fixedEntreePrice && (
                        <InfoItem>
                          <InfoLabel>Fixed Entree Price:</InfoLabel>
                          <InfoValue>
                            {formatCurrency(tier.fixedEntreePrice)}
                          </InfoValue>
                        </InfoItem>
                      )}
                      {tier.fixedSidePrice && (
                        <InfoItem>
                          <InfoLabel>Fixed Side Price:</InfoLabel>
                          <InfoValue>
                            {formatCurrency(tier.fixedSidePrice)}
                          </InfoValue>
                        </InfoItem>
                      )}
                    </InfoGrid>
                  </TierCard>
                ))}
              </>
            )}
          </>
        )}
      </SubSection>
    )
  }

  const renderVolumeRebate = (
    chefId: string,
    rebate: ChefVolumeRebate | null,
  ) => {
    if (!rebate) {
      return <EmptyState>No volume rebate</EmptyState>
    }

    const sectionName = 'volumeRebate'
    const isExpanded = isSectionExpanded(chefId, sectionName)

    return (
      <SubSection>
        <CollapsibleHeader onClick={() => toggleSection(chefId, sectionName)}>
          <SectionHeaderLeft>
            <SmallExpandIcon isExpanded={isExpanded}>▶</SmallExpandIcon>
            <SubSectionTitle>Volume Rebate</SubSectionTitle>
          </SectionHeaderLeft>
          <SectionHeaderRight>
            <SectionBadge>{formatCurrency(rebate.amount)}</SectionBadge>
          </SectionHeaderRight>
        </CollapsibleHeader>

        {isExpanded && (
          <>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Amount:</InfoLabel>
                <InfoValue>{formatCurrency(rebate.amount)}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Updated At:</InfoLabel>
                <InfoValue>{formatDate(rebate.updatedAt)}</InfoValue>
              </InfoItem>
            </InfoGrid>
            {rebate.appliedVolumeTier && (
              <>
                <TiersTitle>Applied Volume Tier</TiersTitle>
                <TierCard>
                  <InfoGrid>
                    <InfoItem>
                      <InfoLabel>Lower Limit:</InfoLabel>
                      <InfoValue>
                        {formatCurrency(rebate.appliedVolumeTier.lowerLimit)}
                      </InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Upper Limit:</InfoLabel>
                      <InfoValue>
                        {formatCurrency(rebate.appliedVolumeTier.upperLimit)}
                      </InfoValue>
                    </InfoItem>
                    {rebate.appliedVolumeTier.discountPercentage && (
                      <InfoItem>
                        <InfoLabel>Discount %:</InfoLabel>
                        <InfoValue>
                          {rebate.appliedVolumeTier.discountPercentage}%
                        </InfoValue>
                      </InfoItem>
                    )}
                  </InfoGrid>
                </TierCard>
              </>
            )}
          </>
        )}
      </SubSection>
    )
  }

  const renderChefPayout = (chef: ChefWithPayout) => {
    const payout = chef.chefPayout
    const override = chef.chefPayoutOverride
    const isExpanded = expandedChefs.has(chef.id)

    if (!payout) {
      return (
        <ChefCard key={chef.id}>
          <ChefHeaderClickable onClick={() => toggleChef(chef.id)}>
            <ChefHeaderLeft>
              <ExpandIcon isExpanded={isExpanded}>▶</ExpandIcon>
              <ChefName>{chef.name}</ChefName>
              <ChefEmail>{chef.email || 'No email'}</ChefEmail>
            </ChefHeaderLeft>
            <ChefHeaderRight>
              {editingChefs[chef.id] && !editingChefs[chef.id].isDeleting && (
                <EditingBadge>EDITING</EditingBadge>
              )}
              {editingChefs[chef.id]?.isDeleting && (
                <DeletingBadge>DELETING</DeletingBadge>
              )}
              {override && !editingChefs[chef.id] && (
                <OverrideBadge>HAS OVERRIDES</OverrideBadge>
              )}
              {override && (
                <PayoutSummary>
                  Total: {formatCurrency(override.totalPayout)}
                </PayoutSummary>
              )}
            </ChefHeaderRight>
          </ChefHeaderClickable>
          {isExpanded && (
            <>
              <YSpacing height="10px" />
              <Section>
                <CollapsibleSectionHeader
                  onClick={() => toggleSection(chef.id, 'payoutDetails')}
                >
                  <SectionHeaderLeft>
                    <SmallExpandIcon
                      isExpanded={isSectionExpanded(chef.id, 'payoutDetails')}
                    >
                      ▶
                    </SmallExpandIcon>
                    <SectionTitle>Payout Details</SectionTitle>
                  </SectionHeaderLeft>
                  <SectionHeaderRight>
                    {override && (
                      <>
                        <SectionBadge>
                          Gross: {formatCurrency(override.grossPay)}
                        </SectionBadge>
                        <SectionBadge>
                          Total: {formatCurrency(override.totalPayout)}
                        </SectionBadge>
                      </>
                    )}
                    {!editingChefs[chef.id] && updateChefPayoutOverrides && (
                      <>
                        <EditButton
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation()
                            startEditing(chef)
                          }}
                        >
                          {override ? 'Edit Overrides' : 'Add Override'}
                        </EditButton>
                        {override && (
                          <DeleteButton
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation()
                              markForDeletion(chef.id)
                            }}
                          >
                            Delete Override
                          </DeleteButton>
                        )}
                      </>
                    )}
                  </SectionHeaderRight>
                </CollapsibleSectionHeader>

                {isSectionExpanded(chef.id, 'payoutDetails') && (
                  <>
                    {editingChefs[chef.id] ? (
                      <>
                        {editingChefs[chef.id].isDeleting ? (
                          <DeletionNotice>
                            <DeletionIcon>⚠️</DeletionIcon>
                            <DeletionText>
                              This override will be <strong>deleted</strong>{' '}
                              when you save all changes. The payout will revert
                              to calculated values.
                            </DeletionText>
                            <Button
                              label="Undo Delete"
                              onClick={() => cancelEditing(chef.id)}
                              padding="8px 16px"
                            />
                          </DeletionNotice>
                        ) : (
                          <>
                            <EditFieldsContainer>
                              {renderEditableField(
                                chef.id,
                                'Head Count',
                                'headCount',
                              )}
                              {renderEditableField(
                                chef.id,
                                'Gross Pay ($)',
                                'grossPay',
                                true,
                              )}
                              {renderEditableField(
                                chef.id,
                                'Processing Fee ($)',
                                'processingFee',
                                true,
                                false,
                                true,
                              )}
                              {renderEditableField(
                                chef.id,
                                'Volume Rebate Amount ($)',
                                'volumeRebateAmount',
                                true,
                                false,
                                true,
                              )}
                              {renderEditableField(
                                chef.id,
                                'Tax ($)',
                                'tax',
                                true,
                                !isTaxable,
                              )}
                              {renderEditableField(
                                chef.id,
                                'Total Payout ($)',
                                'totalPayout',
                                true,
                                true,
                              )}
                            </EditFieldsContainer>
                            <YSpacing height="15px" />
                            {renderChefPayCategoryAndNotes(chef.id, false)}
                            <YSpacing height="15px" />
                            {totalTaxRate > 0 && (
                              <>
                                <TaxCalculationSection>
                                  <TaxRateDisplay>
                                    Food Tax Rate:{' '}
                                    {(totalTaxRate * 100).toFixed(2)}%
                                  </TaxRateDisplay>
                                  <Button
                                    label="Calculate Tax"
                                    onClick={() => calculateTax(chef.id)}
                                    padding="8px 16px"
                                  />
                                </TaxCalculationSection>
                                <YSpacing height="15px" />
                              </>
                            )}
                            <EditActionsContainer>
                              <Button
                                label="Cancel Edit"
                                onClick={() => cancelEditing(chef.id)}
                                disabled={isSaving}
                                padding="10px 20px"
                              />
                            </EditActionsContainer>
                            <YSpacing height="10px" />
                            <HelperText>
                              Note: Changes will be saved when you click
                              &quot;Save All Changes&quot; at the top.
                              Processing Fee and Volume Rebate Amount should be
                              zero or negative. Total Payout is automatically
                              calculated.
                            </HelperText>
                          </>
                        )}
                      </>
                    ) : override ? (
                      <>
                        {renderValue('Head Count', 'N/A', override.headCount)}
                        {renderValue(
                          'Gross Pay',
                          '$0.00',
                          override.grossPay,
                          true,
                        )}
                        {renderValue(
                          'Processing Fee',
                          '$0.00',
                          override.processingFee,
                          true,
                        )}
                        {renderValue(
                          'Volume Rebate Amount',
                          '$0.00',
                          override.volumeRebateAmount,
                          true,
                        )}
                        {renderValue('Tax', '$0.00', override.tax, true)}
                        {renderValue(
                          'Total Payout',
                          '$0.00',
                          override.totalPayout,
                          true,
                        )}
                        <YSpacing height="10px" />
                        {renderValue(
                          'Chef Pay Category',
                          'N/A',
                          override.chefPayCategory,
                        )}
                        {renderValue('Notes', 'N/A', override.notes)}
                      </>
                    ) : (
                      <EmptyState>
                        No payout data available. Click &quot;Add Override&quot;
                        to manually set payout values.
                      </EmptyState>
                    )}
                  </>
                )}
              </Section>

              {override && (
                <>
                  <DividerLine margin="15px 0" />
                  <Section>
                    <SectionTitle>Override Metadata</SectionTitle>
                    <InfoGrid>
                      <InfoItem>
                        <InfoLabel>Updated By:</InfoLabel>
                        <InfoValue>{override.userEmail || 'Unknown'}</InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>Updated At:</InfoLabel>
                        <InfoValue>{formatDate(override.updatedAt)}</InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>Created At:</InfoLabel>
                        <InfoValue>{formatDate(override.createdAt)}</InfoValue>
                      </InfoItem>
                    </InfoGrid>
                  </Section>
                </>
              )}
            </>
          )}
        </ChefCard>
      )
    }

    return (
      <ChefCard key={chef.id}>
        <ChefHeaderClickable onClick={() => toggleChef(chef.id)}>
          <ChefHeaderLeft>
            <ExpandIcon isExpanded={isExpanded}>▶</ExpandIcon>
            <ChefName>{chef.name}</ChefName>
            <ChefEmail>{chef.email || 'No email'}</ChefEmail>
          </ChefHeaderLeft>
          <ChefHeaderRight>
            {editingChefs[chef.id] && !editingChefs[chef.id].isDeleting && (
              <EditingBadge>EDITING</EditingBadge>
            )}
            {editingChefs[chef.id]?.isDeleting && (
              <DeletingBadge>DELETING</DeletingBadge>
            )}
            {payout.hasOverrides && !editingChefs[chef.id] && (
              <OverrideBadge>HAS OVERRIDES</OverrideBadge>
            )}
            <PayoutSummary>
              Total:{' '}
              {formatCurrency(override?.totalPayout || payout.totalPayout)}
            </PayoutSummary>
          </ChefHeaderRight>
        </ChefHeaderClickable>

        {isExpanded && (
          <>
            <YSpacing height="10px" />

            <Section>
              <SectionTitle>Basic Information</SectionTitle>
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>Pay To Name:</InfoLabel>
                  <InfoValue>{payout.payToName}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Order Number:</InfoLabel>
                  <InfoValue>{payout.orderNumber}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>HQ ID:</InfoLabel>
                  <InfoValue>{payout.hqId}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Date:</InfoLabel>
                  <InfoValue>{formatDate(payout.date)}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Updated At:</InfoLabel>
                  <InfoValue>{formatDate(payout.updatedAt)}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Is Taxable:</InfoLabel>
                  <InfoValue>{payout.isTaxable ? 'Yes' : 'No'}</InfoValue>
                </InfoItem>
              </InfoGrid>
            </Section>

            <DividerLine margin="15px 0" />

            <Section>
              <CollapsibleSectionHeader
                onClick={() => toggleSection(chef.id, 'payoutDetails')}
              >
                <SectionHeaderLeft>
                  <SmallExpandIcon
                    isExpanded={isSectionExpanded(chef.id, 'payoutDetails')}
                  >
                    ▶
                  </SmallExpandIcon>
                  <SectionTitle>Payout Details</SectionTitle>
                </SectionHeaderLeft>
                <SectionHeaderRight>
                  <SectionBadge>
                    Gross:{' '}
                    {formatCurrency(override?.grossPay || payout.grossPay)}
                  </SectionBadge>
                  <SectionBadge>
                    Total:{' '}
                    {formatCurrency(
                      override?.totalPayout || payout.totalPayout,
                    )}
                  </SectionBadge>
                  {!editingChefs[chef.id] && updateChefPayoutOverrides && (
                    <>
                      <EditButton
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation()
                          startEditing(chef)
                        }}
                      >
                        Edit Overrides
                      </EditButton>
                      {override && (
                        <DeleteButton
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation()
                            markForDeletion(chef.id)
                          }}
                        >
                          Delete Override
                        </DeleteButton>
                      )}
                    </>
                  )}
                </SectionHeaderRight>
              </CollapsibleSectionHeader>

              {isSectionExpanded(chef.id, 'payoutDetails') && (
                <>
                  {editingChefs[chef.id] ? (
                    <>
                      {editingChefs[chef.id].isDeleting ? (
                        <DeletionNotice>
                          <DeletionIcon>⚠️</DeletionIcon>
                          <DeletionText>
                            This override will be <strong>deleted</strong> when
                            you save all changes. The payout will revert to
                            calculated values.
                          </DeletionText>
                          <Button
                            label="Undo Delete"
                            onClick={() => cancelEditing(chef.id)}
                            padding="8px 16px"
                          />
                        </DeletionNotice>
                      ) : (
                        <>
                          <EditFieldsContainer>
                            {renderEditableField(
                              chef.id,
                              'Head Count',
                              'headCount',
                            )}
                            {renderEditableField(
                              chef.id,
                              'Gross Pay ($)',
                              'grossPay',
                              true,
                            )}
                            {renderEditableField(
                              chef.id,
                              'Processing Fee ($)',
                              'processingFee',
                              true,
                              false,
                              true,
                            )}
                            {renderEditableField(
                              chef.id,
                              'Volume Rebate Amount ($)',
                              'volumeRebateAmount',
                              true,
                              false,
                              true,
                            )}
                            {renderEditableField(
                              chef.id,
                              'Tax ($)',
                              'tax',
                              true,
                              !isTaxable,
                            )}
                            {renderEditableField(
                              chef.id,
                              'Total Payout ($)',
                              'totalPayout',
                              true,
                              true,
                            )}
                          </EditFieldsContainer>
                          <YSpacing height="15px" />
                          {renderChefPayCategoryAndNotes(chef.id, false)}
                          <YSpacing height="15px" />
                          {totalTaxRate > 0 && (
                            <>
                              <TaxCalculationSection>
                                <TaxRateDisplay>
                                  Food Tax Rate:{' '}
                                  {(totalTaxRate * 100).toFixed(2)}%
                                </TaxRateDisplay>
                                <Button
                                  label="Calculate Tax"
                                  onClick={() => calculateTax(chef.id)}
                                  padding="8px 16px"
                                />
                              </TaxCalculationSection>
                              <YSpacing height="15px" />
                            </>
                          )}
                          <EditActionsContainer>
                            <Button
                              label="Cancel Edit"
                              onClick={() => cancelEditing(chef.id)}
                              disabled={isSaving}
                              padding="10px 20px"
                            />
                          </EditActionsContainer>
                          <YSpacing height="10px" />
                          <HelperText>
                            Note: Changes will be saved when you click
                            &quot;Save All Changes&quot; at the top. Processing
                            Fee and Volume Rebate Amount should be zero or
                            negative. Total Payout is automatically calculated.
                          </HelperText>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {renderValue(
                        'Head Count',
                        payout.headCount,
                        override?.headCount,
                      )}
                      {renderValue(
                        'Gross Pay',
                        payout.grossPay,
                        override?.grossPay,
                        true,
                      )}
                      {renderValue(
                        'Processing Fee',
                        payout.processingFee,
                        override?.processingFee,
                        true,
                      )}
                      {renderValue(
                        'Volume Rebate Amount',
                        payout.volumeRebate?.amount,
                        override?.volumeRebateAmount,
                        true,
                      )}
                      {renderValue('Tax', payout.tax, override?.tax, true)}
                      {renderValue(
                        'Total Payout',
                        payout.totalPayout,
                        override?.totalPayout,
                        true,
                      )}
                      <YSpacing height="10px" />
                      {renderValue(
                        'Processing Fee Rate',
                        payout.processingFeeRate
                          ? `${parseFloat(payout.processingFeeRate) * 100}%`
                          : 'N/A',
                      )}
                      {renderValue(
                        'Daily Gross Volume',
                        payout.dailyGrossVolume,
                        undefined,
                        true,
                      )}
                      <YSpacing height="10px" />
                      {renderValue(
                        'Chef Pay Category',
                        payout.chefPayCategory || 'N/A',
                        override?.chefPayCategory,
                      )}
                      {renderValue(
                        'Notes',
                        payout.notes || 'N/A',
                        override?.notes,
                      )}
                    </>
                  )}
                </>
              )}
            </Section>

            {(payout.volumeRebate || override?.volumeRebateAmount) && (
              <>
                <DividerLine margin="15px 0" />
                <Section>
                  <SectionTitle>Volume Rebate Amount</SectionTitle>
                  {renderValue(
                    'Amount',
                    payout.volumeRebate?.amount,
                    override?.volumeRebateAmount,
                    true,
                  )}
                </Section>
              </>
            )}

            {override && (
              <>
                <DividerLine margin="15px 0" />
                <Section>
                  <SectionTitle>Override Metadata</SectionTitle>
                  <InfoGrid>
                    <InfoItem>
                      <InfoLabel>Updated By:</InfoLabel>
                      <InfoValue>{override.userEmail || 'Unknown'}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Updated At:</InfoLabel>
                      <InfoValue>{formatDate(override.updatedAt)}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Created At:</InfoLabel>
                      <InfoValue>{formatDate(override.createdAt)}</InfoValue>
                    </InfoItem>
                  </InfoGrid>
                </Section>
              </>
            )}

            {payout.vendorPricingAgreement && (
              <>
                <DividerLine margin="15px 0" />
                {renderVendorPricingAgreement(
                  chef.id,
                  payout.vendorPricingAgreement,
                )}
              </>
            )}

            {payout.volumeRebate && (
              <>
                <DividerLine margin="15px 0" />
                {renderVolumeRebate(chef.id, payout.volumeRebate)}
              </>
            )}
          </>
        )}
      </ChefCard>
    )
  }

  if (!chefs || chefs.length === 0) {
    return (
      <Container>
        <EmptyState>No chefs with payout data available</EmptyState>
      </Container>
    )
  }

  const renderAdditionalChefPayoutForm = (
    chefId: string,
    edit: AdditionalChefPayoutEdit,
  ) => {
    if (edit.isDeleting) {
      return (
        <DeletionNotice>
          <DeletionIcon>⚠️</DeletionIcon>
          <DeletionText>
            This additional chef payout will be <strong>deleted</strong> when
            you save all changes.
          </DeletionText>
          <Button
            label="Undo Delete"
            onClick={() => cancelEditingAdditional(chefId)}
            padding="8px 16px"
          />
        </DeletionNotice>
      )
    }

    return (
      <>
        <EditFieldsContainer>
          <EditFieldWrapper>
            <CurrencyInputStyled
              type="number"
              placeholder="Head Count"
              value={edit.values.headCount?.toString() || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const { value } = e.target
                const parsedValue = value ? parseInt(value) : undefined
                updateAdditionalChefValue(chefId, 'headCount', parsedValue)
              }}
            />
            <FieldLabel>Head Count</FieldLabel>
          </EditFieldWrapper>
          {renderEditableFieldForAdditional(
            chefId,
            'Gross Pay ($)',
            'grossPay',
            true,
          )}
          {renderEditableFieldForAdditional(
            chefId,
            'Processing Fee ($)',
            'processingFee',
            true,
            false,
            true,
          )}
          {renderEditableFieldForAdditional(
            chefId,
            'Volume Rebate Amount ($)',
            'volumeRebateAmount',
            true,
            false,
            true,
          )}
          {renderEditableFieldForAdditional(
            chefId,
            'Tax ($)',
            'tax',
            true,
            !isTaxable,
          )}
          {renderEditableFieldForAdditional(
            chefId,
            'Total Payout ($)',
            'totalPayout',
            true,
            true,
          )}
        </EditFieldsContainer>
        <YSpacing height="15px" />
        {renderChefPayCategoryAndNotes(chefId, true)}
        <YSpacing height="15px" />
        {totalTaxRate > 0 && (
          <>
            <TaxCalculationSection>
              <TaxRateDisplay>
                Food Tax Rate: {(totalTaxRate * 100).toFixed(2)}%
              </TaxRateDisplay>
              <Button
                label="Calculate Tax"
                onClick={() => calculateTaxForAdditional(chefId)}
                padding="8px 16px"
              />
            </TaxCalculationSection>
            <YSpacing height="15px" />
          </>
        )}
        <EditActionsContainer>
          <Button
            label="Cancel"
            onClick={() => cancelEditingAdditional(chefId)}
            disabled={isSaving}
            padding="10px 20px"
          />
          {!edit.isNew && (
            <DeleteButton
              onClick={() => markAdditionalForDeletion(chefId)}
              disabled={isSaving}
            >
              Delete Payout
            </DeleteButton>
          )}
        </EditActionsContainer>
      </>
    )
  }

  const renderEditableFieldForAdditional = (
    chefId: string,
    label: string,
    fieldName: keyof EditingState,
    isCurrency = false,
    isReadOnly = false,
    shouldBeNegative = false,
  ) => {
    const chefEdit = editingAdditionalChefs[chefId]
    if (!chefEdit) {
      return null
    }

    // Special handling for processing fee with checkbox
    if (fieldName === 'processingFee' && isCurrency) {
      const currentValue = parseFloat(chefEdit.values[fieldName] as string) || 0
      const isManual = chefEdit.values.isManualProcessingFee || false
      const isDisabled = !isManual

      return (
        <EditFieldWrapper key={fieldName}>
          <CurrencyInputStyled
            type="number"
            placeholder={'0'}
            value={chefEdit.values[fieldName]?.toString() || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const { value } = e.target
              let processedValue = value

              if (shouldBeNegative && value && parseFloat(value) > 0) {
                processedValue = (-Math.abs(parseFloat(value))).toString()
              }

              updateAdditionalChefValue(chefId, fieldName, processedValue)
            }}
            disabled={isDisabled}
          />
          <FieldLabel>{label}</FieldLabel>
          <CheckboxWrapper>
            <Checkbox
              type="checkbox"
              id={`manual-processing-fee-additional-${chefId}`}
              checked={isManual}
              onChange={() => toggleManualProcessingFee(chefId, true)}
            />
            <CheckboxLabel
              htmlFor={`manual-processing-fee-additional-${chefId}`}
            >
              Manually override processing fee
            </CheckboxLabel>
          </CheckboxWrapper>
          {shouldBeNegative && currentValue > 0 && (
            <ValidationError>Must be zero or negative</ValidationError>
          )}
          {chefEdit.errorFetchingProcessingFeeRate ? (
            <ProcessingFeeError>
              ⚠️ Unable to fetch the processing fee rate for this chef. Please
              enter the processing fee manually.
            </ProcessingFeeError>
          ) : chefEdit.processingFeeRate ? (
            <ProcessingFeeInfo>
              Processing Fee Rate:{' '}
              {(parseFloat(chefEdit.processingFeeRate) * 100).toFixed(2)}%
              {!isManual && <> • Auto-calculated from gross payout</>}
              {isManual && <> • Manually overridden</>}
            </ProcessingFeeInfo>
          ) : (
            <ProcessingFeeWarning>
              No processing fee rate set for this chef.
            </ProcessingFeeWarning>
          )}
        </EditFieldWrapper>
      )
    }

    // Special handling for volume rebate with checkbox
    if (fieldName === 'volumeRebateAmount' && isCurrency) {
      const currentValue = parseFloat(chefEdit.values[fieldName] as string) || 0
      const isManual = chefEdit.values.isManualVolumeRebate || false
      const isDisabled = !isManual

      return (
        <EditFieldWrapper key={fieldName}>
          <CurrencyInputStyled
            type="number"
            placeholder={label}
            value={isManual ? chefEdit.values[fieldName]?.toString() || '' : ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const { value } = e.target
              let processedValue = value

              if (shouldBeNegative && value && parseFloat(value) > 0) {
                processedValue = (-Math.abs(parseFloat(value))).toString()
              }

              updateAdditionalChefValue(chefId, fieldName, processedValue)
            }}
            disabled={isDisabled}
          />
          <FieldLabel>{label}</FieldLabel>
          <CheckboxWrapper>
            <Checkbox
              type="checkbox"
              id={`manual-volume-rebate-additional-${chefId}`}
              checked={isManual}
              onChange={() => toggleManualVolumeRebate(chefId, true)}
            />
            <CheckboxLabel
              htmlFor={`manual-volume-rebate-additional-${chefId}`}
            >
              Manually override volume rebate
            </CheckboxLabel>
          </CheckboxWrapper>
          {shouldBeNegative && currentValue > 0 && (
            <ValidationError>Must be zero or negative</ValidationError>
          )}
          {isManual ? (
            <VolumeRebateInfo>
              Volume rebate amount is manually overridden
            </VolumeRebateInfo>
          ) : (
            <VolumeRebateInfo>
              Vendor&apos;s applicable volume rebate tier will be applied to
              this payout
            </VolumeRebateInfo>
          )}
        </EditFieldWrapper>
      )
    }

    if (isCurrency) {
      const currentValue = parseFloat(chefEdit.values[fieldName] as string) || 0

      return (
        <EditFieldWrapper key={fieldName}>
          <CurrencyInputStyled
            type="number"
            placeholder={label}
            value={chefEdit.values[fieldName]?.toString() || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const { value } = e.target
              let processedValue = value

              if (shouldBeNegative && value && parseFloat(value) > 0) {
                processedValue = (-Math.abs(parseFloat(value))).toString()
              }

              updateAdditionalChefValue(chefId, fieldName, processedValue)
            }}
            disabled={isReadOnly}
          />
          <FieldLabel>{label}</FieldLabel>
          {shouldBeNegative && currentValue > 0 && (
            <ValidationError>Must be zero or negative</ValidationError>
          )}
        </EditFieldWrapper>
      )
    }

    return null
  }

  return (
    <Container>
      <YSpacing height="20px" />
      <HeaderSection>
        <HeaderLeft>
          <TabDescription>
            Adjust chef gross payout for this order. Fees and taxes are still
            generated for the newly adjusted gross payout amounts but Volume
            Rebates are not - so please include any volume rebates in the gross
            amount if necessary.
          </TabDescription>
          <HeaderText>
            Displaying payout information for {chefs.length} chef(s)
          </HeaderText>
          {totalTaxRate > 0 && (
            <TaxRateInfo>
              Food Tax Rate: {(totalTaxRate * 100).toFixed(2)}%
            </TaxRateInfo>
          )}
        </HeaderLeft>
        {hasAnyChanges() && updateChefPayoutOverrides && (
          <GlobalActionsContainer>
            <Button
              label={isSaving ? 'Saving...' : 'Save All Changes'}
              onClick={saveAllOverrides}
              disabled={isSaving}
              padding="10px 20px"
            />
            <Button
              label="Cancel All"
              onClick={cancelAllEdits}
              disabled={isSaving}
              padding="10px 20px"
            />
            <ChangesIndicator>
              {Object.keys(editingChefs).length +
                Object.keys(editingAdditionalChefs).length}{' '}
              chef(s) with pending changes
            </ChangesIndicator>
          </GlobalActionsContainer>
        )}
      </HeaderSection>

      <YSpacing height="20px" />
      {chefs.map((chef) => renderChefPayout(chef))}

      {/* Additional Chef Payouts Section */}
      {updateChefPayoutOverrides && (
        <>
          <YSpacing height="40px" />
          <DividerLine />
          <YSpacing height="20px" />
          <AdditionalChefsSection>
            <AdditionalChefsHeader>
              <SectionTitle>Additional Chef Payouts</SectionTitle>
              {loadChefs && (
                <Button
                  label="+ Add Chef Payout"
                  onClick={() => setShowAddAdditionalChef(true)}
                  padding="8px 16px"
                  disabled={isSaving || showAddAdditionalChef}
                />
              )}
            </AdditionalChefsHeader>

            {showAddAdditionalChef && loadChefs && (
              <AddChefForm>
                <AddChefFormTitle>Add New Chef Payout</AddChefFormTitle>
                <HelperText>
                  Search for a chef in the current market to add their payout
                  information.
                </HelperText>
                <YSpacing height="15px" />
                <AutocompleteInput
                  label="Search Chef"
                  loaderFunction={(search) => loadChefs({ ...search })}
                  value=""
                  onSelect={(chef: any) => {
                    addNewAdditionalChef({
                      chefId: chef.id,
                      name: chef.name,
                      email: chef.email || '',
                      phone: chef.phone || '',
                    })
                  }}
                />
                <YSpacing height="15px" />
                <EditActionsContainer>
                  <Button
                    label="Cancel"
                    onClick={() => setShowAddAdditionalChef(false)}
                    padding="8px 16px"
                  />
                </EditActionsContainer>
              </AddChefForm>
            )}

            {/* Existing Additional Chefs */}
            {additionalChefPayouts.map((additional) => {
              const isEditing = editingAdditionalChefs[additional.chefId]
              const isExpanded = isEditing !== undefined

              return (
                <ChefCard key={additional.chefId}>
                  <ChefHeaderClickable
                    onClick={() =>
                      !isEditing && startEditingAdditional(additional)
                    }
                  >
                    <ChefHeaderLeft>
                      <ExpandIcon isExpanded={isExpanded}>▶</ExpandIcon>
                      <ChefName>{additional.name}</ChefName>
                      <ChefEmail>{additional.email}</ChefEmail>
                      {additional.isOutOfNetworkPartner && (
                        <SectionBadge>Out of Network</SectionBadge>
                      )}
                    </ChefHeaderLeft>
                    <ChefHeaderRight>
                      {isEditing && !isEditing.isDeleting && (
                        <EditingBadge>EDITING</EditingBadge>
                      )}
                      {isEditing?.isDeleting && (
                        <DeletingBadge>DELETING</DeletingBadge>
                      )}
                      <PayoutSummary>
                        Total:{' '}
                        {formatCurrency(additional.chefPayout?.totalPayout)}
                      </PayoutSummary>
                    </ChefHeaderRight>
                  </ChefHeaderClickable>

                  {!isEditing &&
                    (additional.userEmail || additional.updatedAt) && (
                      <AdditionalPayoutAuditInfo>
                        <AuditInfoLabel>Last Updated:</AuditInfoLabel>
                        <AuditInfoValue>
                          {additional.userEmail || 'Unknown'}
                          {additional.updatedAt && (
                            <AuditInfoTimestamp>
                              {' '}
                              on {formatDate(additional.updatedAt)}
                            </AuditInfoTimestamp>
                          )}
                        </AuditInfoValue>
                      </AdditionalPayoutAuditInfo>
                    )}

                  {isEditing && (
                    <>
                      <YSpacing height="15px" />
                      {renderAdditionalChefPayoutForm(
                        additional.chefId,
                        isEditing,
                      )}
                    </>
                  )}
                </ChefCard>
              )
            })}

            {/* New Additional Chefs Being Created */}
            {Object.entries(editingAdditionalChefs)
              .filter(([, edit]) => edit.isNew)
              .map(([chefId, edit]) => (
                <ChefCard key={chefId}>
                  <ChefHeaderClickable>
                    <ChefHeaderLeft>
                      <ChefName>{edit.name}</ChefName>
                      <ChefEmail>{edit.email}</ChefEmail>
                    </ChefHeaderLeft>
                    <ChefHeaderRight>
                      <EditingBadge>NEW</EditingBadge>
                    </ChefHeaderRight>
                  </ChefHeaderClickable>
                  <YSpacing height="15px" />
                  {renderAdditionalChefPayoutForm(chefId, edit)}
                </ChefCard>
              ))}

            {/* Empty state when no additional chefs */}
            {additionalChefPayouts.length === 0 &&
              Object.values(editingAdditionalChefs).filter((e) => e.isNew)
                .length === 0 &&
              !showAddAdditionalChef && (
                <EmptyState>
                  No additional chef payouts. Click &quot;+ Add Chef
                  Payout&quot; to add one.
                </EmptyState>
              )}
          </AdditionalChefsSection>
        </>
      )}
    </Container>
  )
}

// Styled Components
const Container = styled.div`
  padding: 20px 0;
`

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
`

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const HeaderText = styled.p`
  font-size: 14px;
  color: ${colors.gray400};
  font-weight: bold;
  margin: 0;
`

const TaxRateInfo = styled.p`
  font-size: 13px;
  color: #2196f3;
  font-weight: 600;
  margin: 0;
  padding: 6px 12px;
  background: #e3f2fd;
  border-radius: 4px;
  display: inline-block;
`

const GlobalActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`

const ChangesIndicator = styled.span`
  font-size: 13px;
  color: #ff6b6b;
  font-weight: 600;
  padding: 8px 12px;
  background: #fff5f5;
  border-radius: 6px;
  border: 1px solid #ff6b6b;
`

const ChefCard = styled.div`
  background: ${colors.gray100};
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid ${colors.gray200};
`

const ChefHeaderClickable = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  cursor: pointer;
  padding: 10px;
  margin: -10px;
  border-radius: 8px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${colors.gray200};
  }
`

const ChefHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`

const ChefHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

const ExpandIcon = styled.span<{ isExpanded: boolean }>`
  font-size: 12px;
  color: ${colors.gray400};
  transition: transform 0.2s;
  transform: ${(props) =>
    props.isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'};
  display: inline-block;
  min-width: 12px;
`

const ChefName = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: ${colors.gray400};
  margin: 0;
`

const ChefEmail = styled.p`
  font-size: 14px;
  color: ${colors.gray400};
  margin: 0;
`

const OverrideBadge = styled.span`
  background: #ff6b6b;
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 0.5px;
`

const EditingBadge = styled.span`
  background: #2196f3;
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 0.5px;
`

const DeletingBadge = styled.span`
  background: #f44336;
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 0.5px;
`

const PayoutSummary = styled.span`
  font-size: 14px;
  color: ${colors.gray400};
  font-weight: bold;
  padding: 6px 12px;
  background: white;
  border-radius: 6px;
  border: 1px solid ${colors.gray200};
`

const Section = styled.div`
  margin: 15px 0;
`

const SectionTitle = styled.h4`
  font-size: 16px;
  font-weight: bold;
  color: ${colors.gray400};
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const CollapsibleSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 10px;
  margin: -10px -10px 10px -10px;
  border-radius: 6px;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
  }
`

const CollapsibleHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 8px;
  margin: -8px -8px 10px -8px;
  border-radius: 6px;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
  }
`

const SectionHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const SectionHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const SmallExpandIcon = styled.span<{ isExpanded: boolean }>`
  font-size: 10px;
  color: ${colors.gray400};
  transition: transform 0.2s;
  transform: ${(props) =>
    props.isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'};
  display: inline-block;
  min-width: 10px;
`

const SectionBadge = styled.span`
  font-size: 12px;
  color: ${colors.gray400};
  font-weight: 600;
  padding: 4px 8px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  white-space: nowrap;
`

const SubSection = styled.div`
  margin: 10px 0;
  padding: 15px;
  background: white;
  border-radius: 6px;
  border: 1px solid ${colors.gray200};
`

const SubSectionTitle = styled.h5`
  font-size: 14px;
  font-weight: bold;
  color: ${colors.gray400};
  margin: 0 0 10px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 15px;
  margin-top: 10px;
`

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
`

const InfoLabel = styled.span`
  font-size: 12px;
  color: ${colors.gray400};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`

const InfoValue = styled.span`
  font-size: 14px;
  color: ${colors.gray400};
  font-weight: 500;
`

const ValueRow = styled.div`
  display: flex;
  align-items: center;
  margin: 10px 0;
  padding: 10px;
  background: white;
  border-radius: 4px;
`

const Label = styled.span`
  font-size: 13px;
  color: ${colors.gray400};
  font-weight: 600;
  min-width: 180px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const ValueContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
`

const BaseValue = styled.span<{ hasOverride: boolean }>`
  font-size: 14px;
  color: ${(props) => (props.hasOverride ? colors.gray400 : colors.gray400)};
  font-weight: ${(props) => (props.hasOverride ? 'normal' : 'bold')};
  text-decoration: ${(props) => (props.hasOverride ? 'line-through' : 'none')};
  opacity: ${(props) => (props.hasOverride ? 0.6 : 1)};
`

const Arrow = styled.span`
  font-size: 16px;
  color: #ff6b6b;
  font-weight: bold;
`

const OverrideValue = styled.span`
  font-size: 14px;
  color: #ff6b6b;
  font-weight: bold;
  padding: 4px 8px;
  background: #fff5f5;
  border-radius: 4px;
`

const EmptyState = styled.p`
  font-size: 14px;
  color: ${colors.gray400};
  font-style: italic;
  text-align: center;
  padding: 20px;
`

const TiersTitle = styled.h6`
  font-size: 13px;
  font-weight: bold;
  color: ${colors.gray400};
  margin: 15px 0 10px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const TierCard = styled.div`
  background: #f8f9fa;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 10px;
  border: 1px solid ${colors.gray200};
`

const TierHeader = styled.div`
  font-size: 12px;
  font-weight: bold;
  color: ${colors.gray400};
  margin-bottom: 8px;
  text-transform: uppercase;
`

const EditButton = styled.button`
  background: #4caf50;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  white-space: nowrap;

  &:hover {
    background: #45a049;
  }
`

const DeleteButton = styled.button`
  background: #f44336;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  white-space: nowrap;

  &:hover {
    background: #da190b;
  }
`

const DeletionNotice = styled.div`
  background: #fff5f5;
  border: 2px solid #ff6b6b;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  margin: 15px 0;
`

const DeletionIcon = styled.span`
  font-size: 32px;
`

const DeletionText = styled.p`
  flex: 1;
  margin: 0;
  font-size: 14px;
  color: ${colors.gray400};

  strong {
    color: #f44336;
  }
`

const EditFieldsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 15px;
  margin-top: 15px;
`

const EditFieldWrapper = styled.div`
  width: 100%;
  position: relative;
`

const CurrencyInputStyled = styled.input`
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  border: 1px solid ${colors.gray200};
  border-radius: 4px;
  background: white;
  color: ${colors.gray400};
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #2196f3;
  }

  &:disabled {
    background: ${colors.gray100};
    cursor: not-allowed;
  }

  /* Remove spinner for number inputs */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type='number'] {
    -moz-appearance: textfield;
  }
`

const FieldLabel = styled.label`
  position: absolute;
  top: -8px;
  left: 10px;
  background: white;
  padding: 0 5px;
  font-size: 12px;
  color: ${colors.gray400};
  font-weight: 600;
`

const EditActionsContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`

const HelperText = styled.p`
  font-size: 12px;
  color: ${colors.gray400};
  font-style: italic;
  margin: 5px 0 0 0;
`

const ValidationError = styled.span`
  font-size: 11px;
  color: #f44336;
  margin-top: 4px;
  display: block;
`

const ProcessingFeeError = styled.span`
  font-size: 11px;
  color: #ff9800;
  background: #fff3e0;
  padding: 8px 12px;
  margin-top: 8px;
  display: block;
  border-radius: 4px;
  border-left: 3px solid #ff9800;
  line-height: 1.5;
`

const ProcessingFeeInfo = styled.span`
  font-size: 11px;
  color: #2196f3;
  background: #e3f2fd;
  padding: 8px 12px;
  margin-top: 8px;
  display: block;
  border-radius: 4px;
  border-left: 3px solid #2196f3;
  line-height: 1.5;
`

const ProcessingFeeWarning = styled.span`
  font-size: 11px;
  color: #757575;
  background: #f5f5f5;
  padding: 8px 12px;
  margin-top: 8px;
  display: block;
  border-radius: 4px;
  border-left: 3px solid #9e9e9e;
  line-height: 1.5;
`

const VolumeRebateInfo = styled.span`
  font-size: 11px;
  color: #673ab7;
  background: #ede7f6;
  padding: 8px 12px;
  margin-top: 8px;
  display: block;
  border-radius: 4px;
  border-left: 3px solid #673ab7;
  line-height: 1.5;
`

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 8px 0;
`

const Checkbox = styled.input`
  cursor: pointer;
  width: 16px;
  height: 16px;
`

const CheckboxLabel = styled.label`
  font-size: 12px;
  color: ${colors.gray400};
  cursor: pointer;
  user-select: none;
`

const TaxCalculationSection = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px;
  background: #e3f2fd;
  border-radius: 6px;
  border: 1px solid #2196f3;
`

const TaxRateDisplay = styled.span`
  font-size: 14px;
  color: ${colors.gray400};
  font-weight: 600;
`

const AdditionalChefsSection = styled.div`
  margin-top: 20px;
`

const AdditionalChefsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`

const AddChefForm = styled.div`
  background: #f5f5f5;
  border: 2px dashed ${colors.gray300};
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
`

const AddChefFormTitle = styled.h4`
  font-size: 16px;
  font-weight: bold;
  color: ${colors.gray400};
  margin: 0 0 10px 0;
`

const TabDescription = styled.p`
  font-size: 13px;
  font-weight: bold;
  color: ${colors.gray400};
  line-height: 1.4;
  margin: 10px 0 10px 0;
  padding: 12px;
`

// Chef Pay Category and Notes styled components
const CategoryNotesContainer = styled.div`
  background: #f8f9fa;
  border: 1px solid ${colors.gray200};
  border-radius: 8px;
  padding: 20px;
  margin-top: 10px;
`

const CategoryNotesTitle = styled.h5`
  font-size: 14px;
  font-weight: bold;
  color: ${colors.gray400};
  margin: 0 0 15px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const CategorySelectWrapper = styled.div`
  position: relative;
  margin-bottom: 15px;
`

const CategorySelect = styled.select<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px 14px;
  font-size: 14px;
  border: 2px solid ${(props) => (props.hasError ? '#f44336' : colors.gray200)};
  border-radius: 6px;
  background: white;
  color: ${colors.gray400};
  cursor: pointer;
  transition: border-color 0.2s;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 18px;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? '#f44336' : '#2196f3')};
  }

  &:hover {
    border-color: ${(props) => (props.hasError ? '#f44336' : colors.gray300)};
  }
`

const SelectFieldLabel = styled.label`
  position: absolute;
  top: -10px;
  left: 12px;
  background: #f8f9fa;
  padding: 0 6px;
  font-size: 12px;
  color: ${colors.gray400};
  font-weight: 600;
`

const NotesWrapper = styled.div`
  position: relative;
  margin-top: 15px;
`

const NotesTextarea = styled.textarea<{ hasError?: boolean }>`
  width: 100%;
  min-height: 100px;
  padding: 14px;
  font-size: 14px;
  border: 2px solid ${(props) => (props.hasError ? '#f44336' : colors.gray200)};
  border-radius: 6px;
  background: white;
  color: ${colors.gray400};
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? '#f44336' : '#2196f3')};
  }

  &::placeholder {
    color: ${colors.gray300};
  }
`

const NotesFieldLabel = styled.label`
  position: absolute;
  top: -10px;
  left: 12px;
  background: white;
  padding: 0 6px;
  font-size: 12px;
  color: ${colors.gray400};
  font-weight: 600;
`

const NotesCharCount = styled.span<{ isError?: boolean }>`
  display: block;
  text-align: right;
  font-size: 11px;
  margin-top: 6px;
  color: ${(props) => (props.isError ? '#f44336' : colors.gray300)};
  font-weight: ${(props) => (props.isError ? '600' : 'normal')};
`

const ValidationErrorsContainer = styled.div`
  margin-top: 15px;
  padding: 12px;
  background: #ffebee;
  border: 1px solid #f44336;
  border-radius: 6px;
`

const ValidationErrorItem = styled.p`
  margin: 0;
  padding: 4px 0;
  font-size: 13px;
  color: #c62828;
  font-weight: 500;

  &:before {
    content: '⚠️ ';
  }
`

const AdditionalPayoutAuditInfo = styled.div`
  margin-top: 10px;
  padding: 8px 12px;
  background: #e3f2fd;
  border-radius: 4px;
  border: 1px solid #90caf9;
`

const AuditInfoLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #1565c0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-right: 6px;
`

const AuditInfoValue = styled.span`
  font-size: 12px;
  color: #1976d2;
  font-weight: 500;
`

const AuditInfoTimestamp = styled.span`
  font-size: 11px;
  color: #64b5f6;
  font-style: italic;
`

export default ChefPayoutsTab
